import { useCallback, useMemo } from 'react';
import { usePreservedCallback } from '@toss/react';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import {
  type AchievementStandardWithOrigin,
  type BuildPromptOptions,
} from '@/shared/features/ai-quiz-generation/types';
import { alertDialog } from '@/ui_v3';
import { useAiQuizGeneration } from '@/views/build/hook/useAiQuizGeneration';

import { useAiFormState } from './useAiFormState';
import { useAiGenerationStrategy } from './useAiGenerationStrategy';

/**
 * AI 퀴즈 생성 통합 훅 (리팩토링 버전)
 *
 * BuildAiModal의 복잡도를 줄이기 위해 여러 책임을 가진 하위 훅들을
 * 조율하는 얇은 레이어입니다. 기존 289줄에서 ~100줄로 단순화되었습니다.
 *
 * 책임 분리:
 * - useAiFormState: 폼 상태 관리 (attachment, textPrompt, options)
 * - useAiGenerationStrategy: 생성 로직 라우팅 (file/youtube/query 전략)
 * - useAiQuizGeneration: 생성 상태 관리 (status, items, tryCount)
 *
 * 개선 사항:
 * - 콜백 중복 제거: 공통 콜백을 useAiGenerationStrategy에서 한 번만 생성
 * - Switch 문 제거: 전략 패턴으로 타입별 로직 선택
 * - 타입 단언 제거: Discriminated union narrowing 활용
 * - useMemo 의존성 감소: 16개 → 8개
 * - 응집도 향상: 관련 상태를 각 훅에서 통합 관리
 * - Props drilling 제거: BuildAiCategoryProvider context 사용
 * - selectedAchievementStandards 외부 주입: 모달이 standards 관리 책임 유지
 * - initialPrompt 지원: 랜딩 페이지 자동 트리거 플로우 지원
 *
 * @param params - onSuccess 콜백, selectedAchievementStandards, initialPrompt
 * @returns 통합된 AI 퀴즈 생성 인터페이스
 *
 * @example
 * ```typescript
 * const buildHook = useAiBuild({
 *   onSuccess: () => console.log('Generated!'),
 *   selectedAchievementStandards: [], // 모달에서 주입
 *   initialPrompt: '초등 5학년 과학 문제', // 선택적
 * });
 *
 * // File 첨부
 * await buildHook.handleAttachFile(file);
 * await buildHook.handleGenerate();
 *
 * // YouTube 첨부
 * await buildHook.handleAttachYoutube();
 * await buildHook.handleGenerate();
 *
 * // Prompt만 사용
 * buildHook.handleTextPromptChange('초등 5학년 과학 문제');
 * await buildHook.handleGenerate();
 * ```
 */
export const useAiBuild = ({
  onSuccess,
  selectedAchievementStandards = [],
  initialPrompt,
  topicId,
  initialGenerationId,
  generationMode,
  uiCohort,
  selectedMode,
}: {
  onSuccess: () => void;
  selectedAchievementStandards?: AchievementStandardWithOrigin[];
  initialPrompt?: string;
  topicId?: string;
  initialGenerationId?: string;
  // dropdown 코호트만 값 전달. 미노출 유저는 undefined → 서버 기본 모델 경로.
  generationMode?: 'fast' | 'thorough';
  uiCohort?: 'as_is' | 'dropdown';
  selectedMode?: 'fast' | 'thorough' | 'default';
}) => {
  const { t } = useTranslation(['common', 'build']);

  // 1. 폼 상태 관리 (attachment, textPrompt, options, selectedAchievementStandards)
  const formState = useAiFormState({
    selectedAchievementStandards,
    initialPrompt,
    topicId,
  });

  // 2. 생성 상태 관리 (status, items, tryCount)
  const generationState = useAiQuizGeneration(initialGenerationId);

  /**
   * 성취기준 추가 핸들러
   * 현재 폼 상태를 전달하여 모달에서 추천 기준을 제공
   *
   * Note: handleAdd는 모달이 직접 관리하므로 여기서는 formState만 노출
   */
  const getFormStateForStandards = usePreservedCallback(() => ({
    attachmentState: formState.attachmentState,
    textPrompt: formState.textPrompt,
  }));

  // 4. 생성 전략 관리 (file/youtube/query 라우팅)
  const strategies = useAiGenerationStrategy({
    onGenerateSuccess: generationState.handleGenerateSuccess,
    onGenerateStart: generationState.handleGenerateStart,
    onGenerateError: generationState.handleGenerateError,
    onAttachmentUpdate: formState.updateAttachment,
    onSuccess,
    tryCount: generationState.tryCount.current,
    generationId: generationState.generationId.current,
  });

  /**
   * 현재 attachment 타입에 맞는 전략 선택
   */
  const currentStrategy = strategies[formState.attachmentState.type];

  /**
   * 퀴즈 생성 핸들러
   * 검증 후 현재 전략의 handleGenerate 호출
   */
  const handleGenerate = useCallback(() => {
    // 검증 1: 빈 프롬프트
    if (!formState.canGenerate) {
      alertDialog.open({
        confirmIntent: 'destructive',
        header: {
          hasCloseButton: false,
          title: t('common:ai.validation.emptyPrompt'),
        },
      });
      return;
    }

    // 검증 2: 100개 제한
    if (generationState.generatedItems.length >= 100) {
      alertDialog.open({
        confirmIntent: 'destructive',
        header: {
          hasCloseButton: false,
          title: t('build:build.ai.limitExceed'),
        },
      });
      return;
    }

    logger.track('biz_ai_generation_started', {
      generation_id: generationState.generationId.current,
      source_type: formState.attachmentState.type,
      is_retry: generationState.tryCount.current > 0,
      attempt_no: generationState.tryCount.current,
      quiz_type: formState.options.quizType,
      quiz_count: formState.options.quizCount,
      difficulty: formState.options.quizLevel,
      locale: formState.options.language,
      selected_standard_codes_count: selectedAchievementStandards.length,
      ui_cohort: uiCohort ?? 'as_is',
      selected_mode: selectedMode ?? 'default',
      client_start_ts: Date.now(),
    });
    currentStrategy.handleGenerate({
      attachmentState: formState.attachmentState,
      textPrompt: formState.textPrompt,
      topicId: formState.topicId,
      options: formState.options,
      selectedStandards: selectedAchievementStandards,
      generationMode,
      uiCohort,
    });
  }, [
    formState.canGenerate,
    formState.attachmentState,
    formState.textPrompt,
    formState.options,
    formState.topicId,
    generationState.generatedItems.length,
    generationState.generationId,
    generationState.tryCount,
    currentStrategy,
    selectedAchievementStandards,
    generationMode,
    uiCohort,
    selectedMode,
    t,
  ]);

  /**
   * YouTube 첨부 핸들러
   * 현재 attachment 상태를 전달하여 기존 영상 데이터 재사용
   */
  const handleAttachYoutube = useCallback(async () => {
    await strategies.youtube.handleAttach(formState.attachmentState);
  }, [strategies.youtube, formState.attachmentState]);

  /**
   * Website 첨부 핸들러
   * 현재 attachment 상태를 전달하여 기존 website 데이터 재사용
   */
  const handleAttachWebsite = useCallback(async () => {
    await strategies.website.handleAttach(formState.attachmentState);
  }, [strategies.website, formState.attachmentState]);

  /**
   * 옵션 변경 핸들러
   */
  const handleOptionsChange = useCallback(
    (options: BuildPromptOptions) => {
      formState.updateOptions(options);
    },
    [formState],
  );

  /**
   * 그룹화된 인터페이스 반환
   * 18개 flat 값 → 4개 그룹으로 구조화
   */
  return useMemo(
    () => ({
      /**
       * Attachment 그룹
       * 파일/YouTube/Website 첨부 관련 상태 및 핸들러
       */
      attachment: {
        state: formState.attachmentState,
        isAttached: formState.isAttached,
        handleAttachFile: strategies.file.handleAttach,
        handleAttachYoutube,
        handleAttachWebsite,
        handleDetach: currentStrategy.handleDetach,
      },

      /**
       * Form 그룹
       * 텍스트 프롬프트 및 옵션 관련 상태 및 핸들러
       */
      form: {
        textPrompt: formState.textPrompt,
        onChangePrompt: formState.updateTextPrompt,
        options: formState.options,
        onChangeOptions: handleOptionsChange,
        getFormStateForStandards,
      },

      /**
       * Generation 그룹
       * 퀴즈 생성 실행 및 결과 관련
       */
      generation: {
        handleGenerate: formState.canGenerate ? handleGenerate : undefined,
        status: generationState.generateStatus,
        items: generationState.generatedItems,
        handleReset: generationState.handleReset,
        handleDeleteItem: generationState.handleDeleteItem,
        generationId: generationState.generationId.current,
      },

      /**
       * Standards 그룹
       * 성취기준 선택 관련 (읽기 전용 - 모달이 관리)
       */
      standards: {
        selected: selectedAchievementStandards,
      },
    }),
    [
      formState,
      strategies.file.handleAttach,
      handleAttachYoutube,
      handleAttachWebsite,
      currentStrategy.handleDetach,
      handleOptionsChange,
      selectedAchievementStandards,
      getFormStateForStandards,
      handleGenerate,
      generationState,
    ],
  );
};
